import {
	Button,
	Divider,
	Header,
	Icon,
	Modal,
	Segment,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isEmpty } from 'lodash';
import * as React from 'react';
import ReactDropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import type { DemandeCompte } from '../../interfaces/demandeCompte';
import { postDemandeCompte } from '../../services/demandeCompteService';

interface DemandeCompteModalProps {
	setDemandesComptes: (demandesComptes: DemandeCompte[]) => void;
	demandesComptes: DemandeCompte[];
}

const DemandeCompteModal = ({
	setDemandesComptes,
	demandesComptes,
}: DemandeCompteModalProps) => {
	const [xmlFile, setXmlFile] = React.useState<File>();
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [certificatFile, setCertificatFile] = React.useState<File>();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const handleValidate = async () => {
		setIsLoading(true);
		if (!isEmpty(xmlFile) && xmlFile && certificatFile) {
			try {
				const result = await postDemandeCompte(xmlFile, certificatFile);
				const { utilisateurASupprimer } = result;
				toast.success(
					`Création de demande de compte OK. Ancien utilisateur à supprimer: ${utilisateurASupprimer.prenom} ${utilisateurASupprimer.nom} (${utilisateurASupprimer.login})`,
					{ autoClose: false },
				);
				setDemandesComptes([...demandesComptes, result.demande_compte]);
				setIsOpen(false);
			} catch (e) {
				toast.error(
					getErrorMessage(e, 'Création de la demande de compte impossible.'),
				);
			} finally {
				setIsLoading(false);
				setXmlFile(undefined);
				setCertificatFile(undefined);
			}
		} else {
			toast.error('Un des fichiers est manquant.');
			setIsLoading(false);
		}
	};

	const handleChangeOpen = () => {
		setIsOpen((old) => !old);
	};

	const handleChangeXmlFile = (files: File[]) => {
		setXmlFile(files[0]);
	};

	const handleChangeCertificatFile = (files: File[]) => {
		setCertificatFile(files[0]);
	};

	return (
		<div>
			<Button onClick={handleChangeOpen}>Créer une demande de compte</Button>
			<Modal onClose={handleChangeOpen} open={isOpen}>
				<Modal.Header>Création de demande de compte</Modal.Header>
				<Modal.Content>
					<ReactDropzone
						accept={{
							'text/xml': [],
							'application/xml': [],
							'.xml': [],
						}}
						onDrop={handleChangeXmlFile}
						multiple={false}
					>
						{({ getRootProps, getInputProps }) => (
							<div {...getRootProps()}>
								<Header textAlign="left">Fichier XML</Header>
								<Segment>
									<input {...getInputProps()} />
									<Icon
										iconSet="Lucide"
										name="FolderUp"
										color={xmlFile ? 'green' : 'black'}
									/>
									{xmlFile ? (
										<p>Fichier: {xmlFile.name}</p>
									) : (
										<p>Déposer ici le fichier XML.</p>
									)}
								</Segment>
							</div>
						)}
					</ReactDropzone>
					<Divider hidden />

					<ReactDropzone
						accept={{
							'application/pkix-cert': [],
							'application/x-x509-ca-cert': [],
							'.cer': [],
						}}
						onDrop={handleChangeCertificatFile}
						multiple={false}
					>
						{({ getRootProps, getInputProps }) => (
							<div {...getRootProps()}>
								<Header textAlign="left">Certificat CER</Header>
								<Segment>
									<input {...getInputProps()} />
									<Icon
										iconSet="Lucide"
										name="FolderUp"
										color={certificatFile ? 'green' : 'black'}
									/>
									{certificatFile ? (
										<p>Fichier: {certificatFile.name}</p>
									) : (
										<p>Déposer ici le certificat CER.</p>
									)}
								</Segment>
							</div>
						)}
					</ReactDropzone>
				</Modal.Content>
				<Modal.Actions>
					<Button
						onClick={handleValidate}
						data-testid="valider-admin-modal-btn"
						loading={isLoading}
						primary
					>
						Valider
					</Button>
				</Modal.Actions>
			</Modal>
		</div>
	);
};
export default DemandeCompteModal;
